import { AlertCircle, Check, Clock, Loader2 } from 'lucide-react'
import type { SseStep } from '@/app/services/http/sseClient'
import { Progress } from '@/view/components/ui/progress'
import { cn } from '@/app/utils/cn'

interface StepProgressProps {
  steps: SseStep[]
  title?: string
  className?: string
}

const FALLBACK_LABEL = 'Processando'

function labelOf(step: SseStep) {
  return step.label ?? FALLBACK_LABEL
}

function iconOf(step: SseStep) {
  if (step.state === 'done') {
    return <Check className="size-4 shrink-0 text-primary" />
  }

  if (step.state === 'failed') {
    return <AlertCircle className="size-4 shrink-0 text-destructive" />
  }

  if (step.state === 'queued') {
    return <Clock className="size-4 shrink-0 text-muted-foreground" />
  }

  return <Loader2 className="size-4 shrink-0 animate-spin text-primary" />
}

function hintOf(step: SseStep) {
  if (step.state === 'queued') return 'na fila'
  if (step.state === 'failed') return step.message
  if (step.state === 'done' && step.ms) return `${(step.ms / 1000).toFixed(1)}s`
  return undefined
}

export function StepProgress({ steps, title, className }: StepProgressProps) {
  if (!steps.length) return null

  const settled = steps.filter((step) => step.state === 'done').length
  const percent = Math.round((settled / steps.length) * 100)

  return (
    <div className={cn('flex flex-col gap-3 rounded-md border p-4', className)}>
      <div className="flex flex-col gap-2">
        <span className="text-sm font-medium text-foreground">
          {title ?? 'Processando sua solicitação'}
        </span>
        <Progress value={percent} />
      </div>

      <ul className="flex flex-col gap-2">
        {steps.map((step) => {
          const hint = hintOf(step)

          return (
            <li
              key={step.key}
              className={cn(
                'flex items-center gap-2 text-sm',
                step.state === 'done' && 'text-muted-foreground',
                step.state === 'failed' && 'text-destructive',
              )}
            >
              {iconOf(step)}
              <span className="flex-1 truncate">{labelOf(step)}</span>
              {hint && (
                <span className="shrink-0 text-xs text-muted-foreground">
                  {hint}
                </span>
              )}
            </li>
          )
        })}
      </ul>
    </div>
  )
}
